import * as THREE from "three";
import { Engine } from "../../game/engine";
import { AudioBed } from "../../game/audio";
import {
  HEX_DIRS,
  hexWorldXZ,
  neighborsBounded as hexNeighborsBounded,
  oppositeDir as hexOppositeDir,
} from "../_lib/hex";

/**
 * Lattice — connectivity puzzle, redesigned for 3D depth + engagement.
 * Game 08 of 12. (v2 — feel-pass sweep.)
 *
 * Scoring contract preserved: seed-driven board schedule + rotation
 * events + connectivity check + per-board par bonus. Replay validates
 * unchanged. Visual layer rebuilt: 3D extruded tiles, cylindrical leads,
 * animated rotation, active-path glow, raised source/sink towers.
 *
 * v5(a) — optional pointy-top hex topology gated behind a constructor
 * `kind` flag, mirrored on submit. Default ('square') stays on the
 * live 4-neighbor grid so the existing leaderboard is unaffected.
 */

export const GRID = 5;
export const N_TILES = GRID * GRID;
export const RUN_MS = 90_000;
const TILE_SIZE = 2.0;
const TILE_PADDING = 0.2;
const TILE_THICK = 0.5;

export type LatticeKind = "square" | "hex";

export interface LatticeRotation { t: number; board_idx: number; tile_idx: number }
export interface BoardSolve { t: number; board_idx: number; rotations: number }

/** v5(c): per-board recap line surfaced on the game-over card. */
export interface BoardRecap {
  board_idx: number;
  rotations: number;
  par: number;
  /** True if the board carried a locked tile (every 3rd from idx=2). */
  locked: boolean;
  /** True if the board was a multi-color "rainbow" board (every 4th
   *  from idx=3) — par is tightened by MULTI_COLOR_PAR_FACTOR. */
  multi_color: boolean;
}

export interface LatticeCallbacks {
  onHud: (boardIdx: number, rotations: number, scoreEstimate: number) => void;
  onGameOver: (result: {
    rotations: LatticeRotation[];
    solves: BoardSolve[];
    scoreEstimate: number;
    kind: LatticeKind;
    /** v5(c): one entry per solved board, in solve order. */
    boardRecap: BoardRecap[];
  }) => void;
  /** v5.1 — fired on solve / board-advance / locked-tile bonk so the
   *  bootstrap can announce state to an aria-live region. */
  onAnnounce?: (msg: string) => void;
}

export interface LatticeOptions {
  /** v5.1 — when true, the tile-rotation tween skips animation and the
   *  solve-flash bloom is suppressed. Mechanic unchanged. */
  reducedMotion?: boolean;
}

interface Tile { leads: number[] }
interface Board { idx: number; tiles: Tile[]; source: number; sink: number; par: number; locked_idx?: number; kind: LatticeKind; }

const LOCKED_BOARD_EVERY = 3;
// Mirror of scoring-lattice.ts MULTI_COLOR_* — every 4th board (idx 3, 7,
// 11…) is a rainbow board with a tighter par. The recap (and the score
// estimate) need this to match what the server actually awarded.
const MULTI_COLOR_BOARD_EVERY = 4;
const MULTI_COLOR_PAR_FACTOR = 0.7;
function isMultiColorBoard(board_idx: number): boolean {
  return board_idx > 0 && (board_idx + 1) % MULTI_COLOR_BOARD_EVERY === 0;
}
function parForBoardIdx(board_idx: number): number {
  let par = Math.ceil(N_TILES * 0.3);
  if (isMultiColorBoard(board_idx)) par = Math.max(1, Math.ceil(par * MULTI_COLOR_PAR_FACTOR));
  return par;
}

function makeRng(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function dirCount(kind: LatticeKind): number {
  return kind === "hex" ? HEX_DIRS : 4;
}

export function generateBoard(seed: number, board_idx: number, kind: LatticeKind): Board {
  const seedMix = kind === "hex" ? seed ^ 0x48455800 : seed;
  const rng = makeRng(seedMix ^ ((board_idx + 1) * 0x9e3779b9));
  const D = dirCount(kind);
  const tiles: Tile[] = [];
  for (let i = 0; i < N_TILES; i++) {
    const maxLeads = Math.min(3, D - 1);
    const leadCount = 1 + Math.floor(rng() * maxLeads);
    const dirs = new Set<number>();
    while (dirs.size < leadCount) dirs.add(Math.floor(rng() * D));
    const shift = Math.floor(rng() * D);
    const leads = [...dirs].map((d) => (d + shift) % D);
    tiles.push({ leads });
  }
  let locked_idx: number | undefined;
  if (board_idx >= 2 && board_idx % LOCKED_BOARD_EVERY === 2) {
    let cand: number;
    do { cand = Math.floor(rng() * N_TILES); } while (cand === 0 || cand === N_TILES - 1);
    locked_idx = cand;
  }
  return { idx: board_idx, tiles, source: 0, sink: N_TILES - 1, par: parForBoardIdx(board_idx), locked_idx, kind };
}

function squareNeighborsBounded(idx: number): number[] {
  const row = Math.floor(idx / GRID);
  const col = idx % GRID;
  const out = new Array<number>(4);
  for (let dir = 0; dir < 4; dir++) {
    const [nr, nc] = dir === 0 ? [row - 1, col]
                    : dir === 1 ? [row, col + 1]
                    : dir === 2 ? [row + 1, col]
                    : [row, col - 1];
    out[dir] = nr < 0 || nr >= GRID || nc < 0 || nc >= GRID ? -1 : nr * GRID + nc;
  }
  return out;
}

function neighborsOf(idx: number, kind: LatticeKind): number[] {
  return kind === "hex" ? hexNeighborsBounded(idx, GRID, GRID) : squareNeighborsBounded(idx);
}

function backDir(dir: number, kind: LatticeKind): number {
  return kind === "hex" ? hexOppositeDir(dir) : (dir + 2) % 4;
}

function isConnected(board: Board, rotations: number[]): boolean {
  const D = dirCount(board.kind);
  const leads = board.tiles.map((t, i) => t.leads.map((d) => (d + rotations[i]) % D));
  const visited = new Set<number>([board.source]);
  const q = [board.source];
  while (q.length) {
    const i = q.shift()!;
    if (i === board.sink) return true;
    const ns = neighborsOf(i, board.kind);
    for (const dir of leads[i]) {
      const j = ns[dir];
      if (j < 0) continue;
      if (!leads[j].includes(backDir(dir, board.kind))) continue;
      if (!visited.has(j)) { visited.add(j); q.push(j); }
    }
  }
  return false;
}

function visitedFromSource(board: Board, rotations: number[]): Set<number> {
  const D = dirCount(board.kind);
  const leads = board.tiles.map((t, i) => t.leads.map((d) => (d + rotations[i]) % D));
  const visited = new Set<number>([board.source]);
  const q = [board.source];
  while (q.length) {
    const i = q.shift()!;
    const ns = neighborsOf(i, board.kind);
    for (const dir of leads[i]) {
      const j = ns[dir];
      if (j < 0) continue;
      if (!leads[j].includes(backDir(dir, board.kind))) continue;
      if (!visited.has(j)) { visited.add(j); q.push(j); }
    }
  }
  return visited;
}

interface TileVisual {
  group: THREE.Group;
  base: THREE.Mesh;
  baseMat: THREE.MeshStandardMaterial;
  leadMeshes: THREE.Mesh[];
  leadMats: THREE.MeshStandardMaterial[];
  targetRotY: number;
}

export class Lattice {
  private engine: Engine;
  private audio = new AudioBed();
  private seed: number;
  private boardIdx = 0;
  private board: Board;
  private rots: number[] = Array(N_TILES).fill(0);
  private tiles: TileVisual[] = [];
  private startedAt = 0;
  private rotationEvents: LatticeRotation[] = [];
  private solves: BoardSolve[] = [];
  private boardRotationCount = 0;
  private scoreEstimate = 0;
  private done = false;
  private cb: LatticeCallbacks;
  private canvas: HTMLCanvasElement;
  private solveFlash = 0;
  private kind: LatticeKind;
  // v5.1 keyboard nav state. focusedIdx is the tile under the visual
  // focus ring (null until first arrow-key press, then sticks at the
  // last focused tile across rotations + board advances).
  private focusedIdx: number | null = null;
  private focusRing: THREE.Mesh | null = null;
  private focusRingMat: THREE.MeshBasicMaterial | null = null;
  private reducedMotion = false;

  // v6 — three parallax starfield layers (far / mid / near) for the
  // circuit-board cosmic mood. Per memory feedback-sibling-games-shallow
  // §"real 3D scene with depth cues". Mirrors Phalanx 2bc3ebc.
  private starLayers: THREE.Points[] = [];
  // v6 — cached source-reachable cell count from the most recent
  // applyPathGlow(), read each frame to drive the adaptive drone signal
  // without recomputing the BFS.
  private lastVisitedCount = 1;

  private onPointer = (e: PointerEvent) => this.handleClick(e);
  private onKey = (e: KeyboardEvent) => this.handleKey(e);

  constructor(
    canvas: HTMLCanvasElement,
    cb: LatticeCallbacks,
    seed = Math.floor(Math.random() * 0x7fffffff),
    kind: LatticeKind = "square",
    opts: LatticeOptions = {},
  ) {
    this.canvas = canvas;
    this.cb = cb;
    this.seed = seed;
    this.kind = kind;
    this.reducedMotion = opts.reducedMotion === true;
    this.board = generateBoard(seed, 0, kind);

    this.engine = new Engine(canvas, { onFrame: (dt, t) => this.frame(dt, t) });
    const span = GRID * (TILE_SIZE + TILE_PADDING);
    this.engine.camera.position.set(0, -span * 0.85, span * 1.3);
    this.engine.camera.lookAt(0, 1, 0);
    this.engine.camera.fov = 56;
    this.engine.camera.updateProjectionMatrix();
    this.engine.scene.fog = new THREE.Fog(0x050818, 16, 40);
    this.engine.scene.background = new THREE.Color(0x050818);

    const ambient = new THREE.AmbientLight(0x506080, 0.45);
    this.engine.scene.add(ambient);
    const key = new THREE.DirectionalLight(0xfff0d0, 1.0);
    key.position.set(8, 12, 10);
    this.engine.scene.add(key);
    const fill = new THREE.DirectionalLight(0x4060ff, 0.45);
    fill.position.set(-6, 5, 6);
    this.engine.scene.add(fill);

    const floorGeo = new THREE.CircleGeometry(span * 1.4, 64);
    const floorMat = new THREE.MeshStandardMaterial({ color: 0x020410, roughness: 0.9, metalness: 0.2 });
    const floor = new THREE.Mesh(floorGeo, floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -0.6;
    this.engine.scene.add(floor);

    canvas.addEventListener("pointerdown", this.onPointer);
    window.addEventListener("keydown", this.onKey);

    // v5.1 — focus ring for keyboard navigation. Hidden until first
    // arrow-key press. Rendered as a flat tube above the focused tile.
    const ringGeo = new THREE.RingGeometry(TILE_SIZE * 0.55, TILE_SIZE * 0.62, 32);
    this.focusRingMat = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    });
    this.focusRing = new THREE.Mesh(ringGeo, this.focusRingMat);
    this.focusRing.rotation.x = -Math.PI / 2;
    this.focusRing.position.y = TILE_THICK * 0.55;
    this.engine.scene.add(this.focusRing);

    // v6 — parallax starfield (3 layers). Cool palette with cyan tint to
    // match the lead glow — "circuit-board cosmic." Stars sit behind the
    // FogExp2/Fog falloff (16-40u), so the far layer reads as faint
    // stellar depth and near reads as ambient sparkle. Single static
    // draw per layer; no per-frame GPU cost. fog:false so points
    // persist past the fog ramp instead of disappearing entirely.
    const makeStars = (count: number, z: number, radius: number, sizes: { size: number; color: number }) => {
      const positions = new Float32Array(count * 3);
      let s = (seed ^ Math.imul(z | 0, 0x9e3779b1)) | 0;
      const r = () => {
        s = (s + 0x6d2b79f5) | 0;
        let t = s;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
      };
      for (let i = 0; i < count; i++) {
        positions[i * 3] = (r() - 0.5) * radius * 2;
        positions[i * 3 + 1] = (r() - 0.5) * radius * 2;
        positions[i * 3 + 2] = z + (r() - 0.5) * 8;
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      const mat = new THREE.PointsMaterial({
        color: sizes.color, size: sizes.size, sizeAttenuation: true,
        transparent: true, opacity: 0.85, depthWrite: false, fog: false,
      });
      const pts = new THREE.Points(geo, mat);
      this.engine.scene.add(pts);
      this.starLayers.push(pts);
    };
    makeStars(600, -180, 240, { size: 0.6,  color: 0x7088b0 });  // far — cool blue
    makeStars(300,  -90, 140, { size: 0.5,  color: 0x90a8d0 });  // mid
    makeStars(120,  -45,  90, { size: 0.45, color: 0xa0c0e8 });  // near — cyan-tinted
  }

  private moveFocus(dr: number, dc: number) {
    if (this.done) return;
    const curr = this.focusedIdx ?? 0;
    const row = Math.floor(curr / GRID);
    const col = curr % GRID;
    const nr = Math.max(0, Math.min(GRID - 1, row + dr));
    const nc = Math.max(0, Math.min(GRID - 1, col + dc));
    this.focusedIdx = nr * GRID + nc;
    this.updateFocusRing();
  }

  private updateFocusRing() {
    if (!this.focusRing || !this.focusRingMat) return;
    if (this.focusedIdx === null) {
      this.focusRingMat.opacity = 0;
      return;
    }
    const { x, z } = this.tileWorldPos(this.focusedIdx);
    this.focusRing.position.set(x, TILE_THICK * 0.55, z);
    this.focusRingMat.opacity = 0.85;
  }

  private handleKey(e: KeyboardEvent) {
    if (this.done) return;
    if (e.target instanceof HTMLInputElement) return;
    switch (e.code) {
      case "ArrowUp":    e.preventDefault(); this.moveFocus(-1, 0); break;
      case "ArrowDown":  e.preventDefault(); this.moveFocus(1, 0);  break;
      case "ArrowLeft":  e.preventDefault(); this.moveFocus(0, -1); break;
      case "ArrowRight": e.preventDefault(); this.moveFocus(0, 1);  break;
      case "Space":
      case "Enter":
      case "KeyR":
        if (this.focusedIdx !== null) {
          e.preventDefault();
          this.rotateTile(this.focusedIdx);
        }
        break;
      case "Escape":
        // Esc gives up — ends the run early, surfacing whatever's been
        // solved so far via the existing finish() path.
        e.preventDefault();
        this.finish();
        break;
    }
  }

  private tileWorldPos(idx: number): { x: number; z: number } {
    if (this.kind === "hex") {
      // Use hex helper directly. cellSize = TILE_SIZE + TILE_PADDING.
      const q = idx % GRID;
      const r = Math.floor(idx / GRID);
      return hexWorldXZ(q, r, GRID, GRID, TILE_SIZE + TILE_PADDING);
    }
    const row = Math.floor(idx / GRID);
    const col = idx % GRID;
    const step = TILE_SIZE + TILE_PADDING;
    const offset = (GRID - 1) / 2;
    return { x: (col - offset) * step, z: (row - offset) * step };
  }

  /** Lead direction unit-vector in tile-local x/z space (pre-rotation). */
  private leadDirVec(dir: number): { x: number; z: number } {
    if (this.kind === "hex") {
      // Pointy-top hex: 6 directions at 60° increments.
      // dir 0=NE, 1=E, 2=SE, 3=SW, 4=W, 5=NW
      // Convert to angles in the (x, z) plane. E is +x. North is -z.
      // Pointy-top: directions at 30°, 90°, 150°, 210°, 270°, 330° from +x.
      const ang = (Math.PI / 180) * (30 + dir * 60);
      return { x: Math.cos(ang), z: -Math.sin(ang) };
    }
    // Square: dir 0=N (-z), 1=E (+x), 2=S (+z), 3=W (-x)
    return dir === 0 ? { x: 0, z: -1 }
         : dir === 1 ? { x: 1, z: 0 }
         : dir === 2 ? { x: 0, z: 1 }
         : { x: -1, z: 0 };
  }

  private buildBoard() {
    for (const t of this.tiles) {
      this.engine.scene.remove(t.group);
      t.base.geometry.dispose();
      t.baseMat.dispose();
      for (const lm of t.leadMeshes) lm.geometry.dispose();
      for (const lm of t.leadMats) lm.dispose();
    }
    this.tiles = [];

    for (let i = 0; i < N_TILES; i++) {
      const { x, z } = this.tileWorldPos(i);
      const isSource = i === this.board.source;
      const isSink = i === this.board.sink;
      const isLocked = this.board.locked_idx === i;
      const tileH = isSource || isSink || isLocked ? 1.0 : TILE_THICK;
      const tileFace = this.kind === "hex" ? TILE_SIZE * 0.92 : TILE_SIZE - 0.12;
      // Hex tile geometry — 6-sided cylinder; square stays box.
      const geo = this.kind === "hex"
        ? new THREE.CylinderGeometry(tileFace / 2, tileFace / 2, tileH, 6)
        : new THREE.BoxGeometry(tileFace, tileH, tileFace);
      const baseColor = isSource ? 0xff5b3a : isSink ? 0xffd54a : isLocked ? 0x9a72ff : 0x1a2030;
      const emissive = isSource ? 0xaa2010 : isSink ? 0xff8800 : isLocked ? 0x4030cc : 0x081020;
      const mat = new THREE.MeshStandardMaterial({
        color: baseColor,
        emissive,
        emissiveIntensity: isSource || isSink ? 1.4 : isLocked ? 1.2 : 0.4,
        roughness: 0.55,
        metalness: 0.4,
      });
      const base = new THREE.Mesh(geo, mat);
      base.position.y = tileH / 2;
      const group = new THREE.Group();
      group.add(base);
      group.position.set(x, 0, z);
      this.engine.scene.add(group);

      const leadMeshes: THREE.Mesh[] = [];
      const leadMats: THREE.MeshStandardMaterial[] = [];
      const leadLen = tileFace / 2 - 0.05;
      const half = leadLen / 2;
      for (const dir of this.board.tiles[i].leads) {
        const leadGeo = new THREE.CylinderGeometry(0.12, 0.12, leadLen, 12);
        const leadMat = new THREE.MeshStandardMaterial({
          color: 0x66e0ff,
          emissive: 0x0080cc,
          emissiveIntensity: 0.7,
          roughness: 0.3,
          metalness: 0.55,
        });
        const lead = new THREE.Mesh(leadGeo, leadMat);
        const vec = this.leadDirVec(dir);
        // Position the lead halfway out along its direction.
        lead.position.set(vec.x * half, tileH * 0.5 + 0.05, vec.z * half);
        // Cylinder default axis is +y; orient it to point along (vec.x, 0, vec.z).
        // Yaw the cylinder so its long axis lies in the xz plane along vec.
        const angle = Math.atan2(vec.z, vec.x);
        lead.rotation.set(0, 0, Math.PI / 2);
        lead.rotation.y = -angle;
        base.add(lead);
        leadMeshes.push(lead);
        leadMats.push(leadMat);
      }
      this.tiles.push({ group, base, baseMat: mat, leadMeshes, leadMats, targetRotY: 0 });
    }
    this.applyPathGlow();
  }

  private applyPathGlow() {
    const visited = visitedFromSource(this.board, this.rots);
    this.lastVisitedCount = visited.size;
    for (let i = 0; i < N_TILES; i++) {
      const t = this.tiles[i];
      const lit = visited.has(i);
      const isSpecial = i === this.board.source || i === this.board.sink;
      if (!isSpecial) {
        t.baseMat.emissiveIntensity = lit ? 1.1 : 0.4;
        t.baseMat.emissive.setHex(lit ? 0x00aa88 : 0x081020);
      }
      for (const lm of t.leadMats) {
        lm.emissiveIntensity = lit ? 1.4 : 0.6;
        lm.color.setHex(lit ? 0x46ffe0 : 0x66e0ff);
      }
    }
  }

  private handleClick(e: PointerEvent) {
    if (this.done) return;
    const rect = this.canvas.getBoundingClientRect();
    const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    const ny = -(((e.clientY - rect.top) / rect.height) * 2 - 1);
    const ndc = new THREE.Vector3(nx, ny, 0.5).unproject(this.engine.camera);
    const dir = ndc.sub(this.engine.camera.position).normalize();
    const dist = -this.engine.camera.position.y / dir.y;
    const wx = this.engine.camera.position.x + dir.x * dist;
    const wz = this.engine.camera.position.z + dir.z * dist;
    let best = -1;
    let bestD = Infinity;
    for (let i = 0; i < N_TILES; i++) {
      const { x, z } = this.tileWorldPos(i);
      const d = (wx - x) ** 2 + (wz - z) ** 2;
      if (d < bestD) { bestD = d; best = i; }
    }
    if (best < 0 || bestD > TILE_SIZE * TILE_SIZE) return;
    this.rotateTile(best);
  }

  private rotateTile(tile_idx: number) {
    if (this.board.locked_idx === tile_idx) {
      this.audio.thud();
      const t = this.tiles[tile_idx];
      const base = t.base;
      // Reduced motion suppresses the scale-pop on locked tiles.
      if (!this.reducedMotion) {
        base.scale.set(1.1, 1.1, 1.1);
        setTimeout(() => base.scale.set(1, 1, 1), 120);
      }
      this.cb.onAnnounce?.("Tile is locked and can't rotate.");
      return;
    }
    const t = Math.round(performance.now() - this.startedAt);
    const D = dirCount(this.board.kind);
    this.rots[tile_idx] = (this.rots[tile_idx] + 1) % D;
    this.boardRotationCount += 1;
    this.rotationEvents.push({ t, board_idx: this.boardIdx, tile_idx });
    // Step rotation by one direction tick: 2π/D radians (90° square / 60° hex).
    const stepAngle = (2 * Math.PI) / D;
    this.tiles[tile_idx].targetRotY = -this.rots[tile_idx] * stepAngle;
    this.audio.chime(2, 0);
    this.applyPathGlow();

    if (isConnected(this.board, this.rots)) {
      const savings = Math.max(0, this.board.par - this.boardRotationCount);
      const lockedBonus = this.board.locked_idx !== undefined ? 250 : 0;
      this.scoreEstimate += 200 + savings * 30 + lockedBonus;
      this.solves.push({ t, board_idx: this.boardIdx, rotations: this.boardRotationCount });
      this.audio.chime(12, 0);
      if (lockedBonus > 0) this.audio.chime(19, 0.3);
      // Reduced motion: skip the bloom-flash; the chime + announce
      // still mark the solve.
      if (!this.reducedMotion) this.solveFlash = 1;
      this.cb.onHud(this.boardIdx + 1, this.boardRotationCount, this.scoreEstimate);
      this.cb.onAnnounce?.(
        `Board ${this.boardIdx + 1} solved in ${this.boardRotationCount} moves` +
        (savings > 0 ? ` (${savings} under par)` : "") + ".",
      );
      // Reduced motion advances immediately so a screen reader user
      // isn't waiting for a flourish they can't see.
      setTimeout(() => this.advanceBoard(), this.reducedMotion ? 80 : 500);
      return;
    }
    this.cb.onHud(this.boardIdx + 1, this.boardRotationCount, this.scoreEstimate);
  }

  private advanceBoard() {
    this.boardIdx += 1;
    this.board = generateBoard(this.seed, this.boardIdx, this.kind);
    this.rots = Array(N_TILES).fill(0);
    this.boardRotationCount = 0;
    this.buildBoard();
    // Refresh the focus ring position on the new board's tiles.
    this.updateFocusRing();
    this.cb.onAnnounce?.(
      `Board ${this.boardIdx + 1}. Par: ${this.board.par} moves.` +
      (this.board.locked_idx !== undefined ? " One tile is locked." : ""),
    );
  }

  start() {
    this.audio.arm();
    // v6 — baseline drone; frame() ramps it up adaptively based on
    // board progress + active-path engagement. Replaces the previous
    // fixed 0.4 seed.
    this.audio.setDroneIntensity(0.15);
    this.startedAt = performance.now();
    this.boardIdx = 0;
    this.board = generateBoard(this.seed, 0, this.kind);
    this.rots = Array(N_TILES).fill(0);
    this.rotationEvents = [];
    this.solves = [];
    this.boardRotationCount = 0;
    this.scoreEstimate = 0;
    this.done = false;
    this.buildBoard();
    this.engine.start();
  }

  private frame(dt: number, _t: number) {
    if (this.done) return;
    const elapsed = performance.now() - this.startedAt;
    if (elapsed > RUN_MS) { this.finish(); return; }

    // v6 — adaptive escalation signal blends per-board ramp (each solve
    // ratchets up) with within-board active-path ratio (how much of the
    // grid is currently lit from the source). Drives engine.setBoost
    // (bloom / afterimage) and the audio drone bed. Per memory
    // feedback-sibling-games-shallow §"at least one escalation hook".
    const boardRamp = Math.min(1, this.boardIdx / 8);
    const activeRatio = Math.max(0, Math.min(1, (this.lastVisitedCount - 1) / Math.max(1, N_TILES - 1)));
    const signal = boardRamp * 0.6 + activeRatio * 0.4;
    this.engine.setBoost(signal, dt);
    try { this.audio.setDroneIntensity(0.15 + signal * 0.55); } catch { /* off */ }

    for (const t of this.tiles) {
      const cur = t.base.rotation.y;
      const diff = t.targetRotY - cur;
      // Reduced motion snaps; default eases over ~80 ms.
      if (Math.abs(diff) > 0.001) {
        if (this.reducedMotion) t.base.rotation.y = t.targetRotY;
        else t.base.rotation.y += diff * Math.min(1, dt * 12);
      }
    }

    if (this.solveFlash > 0) {
      this.solveFlash = Math.max(0, this.solveFlash - dt * 1.5);
      for (const t of this.tiles) {
        t.baseMat.emissiveIntensity = Math.max(t.baseMat.emissiveIntensity, 1.5 + this.solveFlash * 1.5);
      }
    }
  }

  private finish() {
    this.done = true;
    this.audio.setDroneIntensity(0);
    // v5(c): build per-board recap in solve order. par/locked/multi_color
    // are derived from the deterministic board schedule so the recap is
    // consistent with what the server's validator awarded.
    const boardRecap: BoardRecap[] = this.solves.map((s) => {
      const board = generateBoard(this.seed, s.board_idx, this.kind);
      return {
        board_idx: s.board_idx,
        rotations: s.rotations,
        par: parForBoardIdx(s.board_idx),
        locked: board.locked_idx !== undefined,
        multi_color: isMultiColorBoard(s.board_idx),
      };
    });
    this.cb.onGameOver({
      rotations: this.rotationEvents,
      solves: this.solves,
      scoreEstimate: this.scoreEstimate,
      kind: this.kind,
      boardRecap,
    });
  }

  setMuted(b: boolean) { this.audio.setMuted(b); }
  setVolume(v: number) { this.audio.setVolume(v); }
  armAudio() { this.audio.arm(); }

  dispose() {
    this.canvas.removeEventListener("pointerdown", this.onPointer);
    window.removeEventListener("keydown", this.onKey);
    try { this.audio.setDroneIntensity(0); } catch { /* off */ }
    if (this.focusRing) {
      this.engine.scene.remove(this.focusRing);
      this.focusRing.geometry.dispose();
      this.focusRingMat?.dispose();
      this.focusRing = null;
      this.focusRingMat = null;
    }
    // v6 — drop the parallax star layers so navigate-away → re-mount
    // doesn't accumulate THREE.Points across runs.
    for (const layer of this.starLayers) {
      this.engine.scene.remove(layer);
      layer.geometry.dispose();
      (layer.material as THREE.Material).dispose();
    }
    this.starLayers = [];
    this.engine.dispose();
  }
}
